import { useEffect, useState, useCallback } from 'react';

/**
 * Wake Lock Hook - Prevents screen from sleeping during workouts
 * Returns: { isActive, isSupported, error, request, release }
 */
export function useWakeLock() {
  const [wakeLock, setWakeLock] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState(null);

  // Check if Wake Lock API is supported
  useEffect(() => {
    const supported = 'wakeLock' in navigator;
    setIsSupported(supported);
    if (!supported) {
      console.warn('Wake Lock API not supported on this device/browser');
    }
  }, []);

  // Request wake lock
  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) {
      setError('Wake Lock API not supported');
      return false;
    }

    try {
      const lock = await navigator.wakeLock.request('screen');
      setWakeLock(lock);
      setIsActive(true);
      setError(null);
      console.log('✅ Wake Lock activated - screen will stay on');

      lock.addEventListener('release', () => {
        console.log('⚠️ Wake Lock released');
        setIsActive(false);
        setWakeLock(null);
      });

      return true;
    } catch (err) {
      console.error('❌ Wake Lock error:', err.message);
      setError(err.message);
      setIsActive(false);
      return false;
    }
  }, []);

  // Release wake lock
  const releaseWakeLock = useCallback(async () => {
    if (wakeLock) {
      try {
        await wakeLock.release();
        setWakeLock(null);
        setIsActive(false);
        console.log('Wake Lock manually released');
      } catch (err) {
        console.error('Error releasing wake lock:', err);
      }
    }
  }, [wakeLock]);

  // Auto-request on mount and handle visibility changes
  useEffect(() => {
    // Initial request
    requestWakeLock();

    // Re-acquire wake lock when page becomes visible (after tab switch, etc.)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('Page visible - re-requesting wake lock');
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) {
        wakeLock.release().catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isActive,
    isSupported,
    error,
    request: requestWakeLock,
    release: releaseWakeLock,
    // Legacy: return the lock object for backward compatibility
    lock: wakeLock
  };
}

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const requestFullscreen = async () => {
    try {
      const elem = document.documentElement;
      
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen(); // Safari
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen(); // IE11
      }
      
      setIsFullscreen(true);
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      
      setIsFullscreen(false);
    } catch (err) {
      console.error('Exit fullscreen error:', err);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  return { isFullscreen, requestFullscreen, exitFullscreen };
}

// Re-export GPS hook for convenience
export { useGPS, GPS_SOURCE, calculateDistance } from './useGPS';
